import { useEffect } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'

import { useApi } from '@tabeeb/modules/shared/utils/API'
import { useDispatch } from '@tabeeb/store'
import { trainingMaterialTitleFormatter } from '@tabeeb/modules/shared/utils/text'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { queryClient } from '@tabeeb/services/queryClient'

import { ArticlesQueryKeys } from '../queries'

export interface UseContentTrainingMaterialsProps {
  enabled?: boolean
  skip?: number
  take?: number
  search?: string | null
  contentId?: number
  onError?: (error: unknown) => void
}

export const useContentTrainingMaterials = ({
  enabled,
  skip,
  take,
  search,
  contentId,
  onError,
}: UseContentTrainingMaterialsProps) => {
  const dispatch = useDispatch()

  const { articleServiceApi: api } = useApi()

  const query = useQuery({
    queryKey: [ArticlesQueryKeys.ContentTrainingMaterials, skip, take, search, contentId],
    queryFn: () =>
      api.contentTrainingMaterials.getAllAsync({
        skip: skip ?? 0,
        take: take ?? 50,
        search: search ?? '',
        contentId,
      }),
    enabled,
    onError,
  })

  useEffect(() => {
    queryClient.setQueryData<number>([ArticlesQueryKeys.UnreadTrainingMaterialsCount, contentId], () => {
      return query.data?.Items.filter((item) => item.Read === false).length ?? 0
    })
  }, [contentId, query.data])

  const detachMutation = useMutation({
    mutationFn: (contentTrainingMaterial: { Id: number }) =>
      api.contentTrainingMaterials.detachAsync(contentTrainingMaterial.Id),
    onSuccess: () => {
      query.refetch()
    },
    onError: () => {
      dispatch(
        onAddErrorNotification({ message: trainingMaterialTitleFormatter.format('Failed to detach training material') })
      )
    },
  })

  return {
    loading: query.isLoading,
    loaded: query.isSuccess,
    totalCount: query.data?.TotalCount ?? 0,
    contentTrainingMaterials: query.data?.Items ?? [],
    onDetach: detachMutation.mutate,
    onReload: query.refetch,
  }
}

export default useContentTrainingMaterials
